import { Grid } from '@mui/material';
import { ColumnSelector } from '@components/columnSelector';
import { GridColDef } from '@mui/x-data-grid';
import { useTableStore } from '@components/table/StoreTable/TableWithStore';
import { SearchBar } from '@components/search/SearchBar';
import { QuickFilters, QuickFiltersProps } from '@components/table/QuickFilters';
import { TableFilter, TFilterValues, TTableBase } from '@components/table/types';
import { TableFilters } from '@components/table/TableFilters';
import { TableProps } from '@components/table/Table';
import { Filter, FilterSolid } from '@assets/svgs';
import { IconButton } from '@components/iconButton';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { Tooltip } from '@components/tooltip/Tooltip';
import { TableCreateFiltersModal } from '@components/table/TableCreateFiltersModal/TableCreateFiltersModal.tsx';
import { ShareButton } from '@components/table/StoreTable/ShareButton';
import {
  CustomFilterToolbar,
  CustomFilterToolbarProps,
} from '@components/table/TableCreateFiltersModal/CustomFilterToolbar/CustomFilterToolbar';

export type StoreTableToolbarProps<T extends TTableBase> = {
  quickFilterGroup?: QuickFiltersProps['quickFilterGroup'];
  tableFilterTypeMetaDataBuilder?: CustomFilterToolbarProps['tableFilterTypeMetaDataBuilder'];
  updateCustomFilters: (customFilters: TFilterValues) => void;
  columns: GridColDef[];
  columnSelectorConfig: {
    columnSelector: any;
    selectedTab: any;
    defaultDisabledKey: any;
    /** Used to hide columns from the selector menu */
    hiddenFromSelectionKeys?: string[];
  } | null;
  selectedTab: string;
  filterConfig?: TableProps<T>['filterConfig'];
  isLoading: boolean;
  adjustTableHeaderHeight: () => void;
};

export function StoreTableToolbar<T extends TTableBase>({
  columns,
  columnSelectorConfig,
  updateCustomFilters,
  quickFilterGroup,
  tableFilterTypeMetaDataBuilder,
  selectedTab,
  filterConfig,
  isLoading,
  adjustTableHeaderHeight,
}: StoreTableToolbarProps<T>) {
  const { isMobileScreen } = useGetScreenSize();
  const [searchParamsInTheUrl, setSearchParamsInTheUrl] = useSearchParams();

  // note: this display mode does not sync with the store and always starts closed
  const [showModalFiltersAnchorEl, setShowModalFiltersAnchorEl] = useState<null | HTMLElement>(null);

  const {
    hiddenColumns,
    updateHiddenColumns,
    filtersLoaded,
    updateFiltersLoaded,
    showFilters,
    updateShowFilters,
    appliedFilters,
  } = useTableStore((state) => ({
    hiddenColumns: state.hiddenColumns,
    updateHiddenColumns: state.updateHiddenColumns,
    filtersLoaded: state.filtersLoaded,
    updateFiltersLoaded: state.updateFiltersLoaded,
    showFilters: state.showFilters,
    updateShowFilters: state.updateShowFilters,
    appliedFilters: state.appliedFilters,
  }));

  const defaultFilters = filterConfig?.defaultFilters;

  const showInlineFilters = filterConfig?.displayMode !== 'Modal' && showFilters;

  useEffect(() => {
    if (filtersLoaded) {
      return;
    }

    if (Object.keys(defaultFilters ?? {}).length) {
      updateShowFilters(true);
    }

    updateFiltersLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFilters, filtersLoaded]);

  const selectableColumns =
    columns?.filter(
      (x) =>
        x.headerName &&
        (!columnSelectorConfig?.hiddenFromSelectionKeys ||
          !columnSelectorConfig.hiddenFromSelectionKeys.includes(x.field)),
    ) ?? [];

  const selectableColumnsKeys: string[] = (selectableColumns ?? []).map((x) => x.field!);

  const selectedColumns = columnSelectorConfig?.columnSelector?.[selectedTab] ?? {};

  const selectedColumnVisibility = Object.keys(selectedColumns).reduce(
    (acc, key) => {
      acc[key] = hiddenColumns?.[key] !== true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const showFilterToggle = filterConfig && filterConfig.filters?.length && filterConfig.filters?.length > 0;

  const searchQueryValue = searchParamsInTheUrl.get('searchText') || undefined;

  const displayInModal = filterConfig?.displayMode === 'Modal';
  const displayCustomFilterBar = displayInModal && !!tableFilterTypeMetaDataBuilder;

  const handleFilterChange = (key: string, value: any) => {
    if (value) {
      if (typeof value === 'object') {
        switch (value.type) {
          case TableFilter.DATE_RANGE: {
            if (value.min || value.max) {
              searchParamsInTheUrl.set(
                key,
                `${value?.min ? value.min?.toISOString() : null},${value?.max ? value.max?.toISOString() : null}`,
              );
            } else {
              searchParamsInTheUrl.delete(key);
            }

            break;
          }
          case TableFilter.NUMBER_RANGE: {
            if (value.min || value.max) {
              searchParamsInTheUrl.set(key, `${value?.min ? value.min : null},${value?.max ? value.max : null}`);
            } else {
              searchParamsInTheUrl.delete(key);
            }

            break;
          }

          default:
            if (value?.value) {
              searchParamsInTheUrl.set(key, value.value);
            } else {
              searchParamsInTheUrl.delete(key);
            }

            break;
        }
      } else {
        searchParamsInTheUrl.set(key, value?.toString());
      }
    } else {
      searchParamsInTheUrl.delete(key);
    }
    setSearchParamsInTheUrl(searchParamsInTheUrl, { replace: true });

    updateCustomFilters({ [key]: value });
  };

  // if the page width changes from browser resize then run adjustTableHeaderHeight
  useEffect(() => {
    const handleResize = () => {
      adjustTableHeaderHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adjustTableHeaderHeight]);

  return (
    <Grid
      container
      id='store-table-toolbar'
      sx={{
        ...gridToolbarContainer,
        paddingTop: { xs: 0, sm: 1 },
        paddingBottom: { xs: 0, sm: 1 },
        paddingRight: 1.5,
        display: 'block',
      }}
    >
      <Grid container spacing={{ xs: 0, sm: 2 }} sx={{ ...chipToolbarContainer }}>
        {isMobileScreen ? null : (
          <Grid flexGrow={1} sx={{ paddingTop: '0px !important', flex: 1 }}>
            {quickFilterGroup && (
              <QuickFilters
                quickFilterGroup={quickFilterGroup}
                defaultFilters={filterConfig?.defaultFilters}
                onChange={(quickFilters: TFilterValues) => {
                  updateCustomFilters(quickFilters);
                }}
              />
            )}
          </Grid>
        )}
        <Grid sx={{ ...filtersToolbarContainer }}>
          <SearchBar
            size={isMobileScreen ? 'small' : 'medium'}
            defaultValue={searchQueryValue}
            onFilterChange={handleFilterChange}
            alwaysOpen={isMobileScreen}
          />

          {isMobileScreen ? null : (
            <>
              {showFilterToggle && (
                <Tooltip
                  title={showInlineFilters ? 'Hide Filters' : displayInModal ? 'Create Custom Filters' : 'Show Filters'}
                  placement='top'
                >
                  <IconButton
                    id='search-button'
                    variant='circular'
                    onClick={(e) => {
                      if (displayInModal) {
                        setShowModalFiltersAnchorEl(e.currentTarget);
                      } else {
                        updateShowFilters(!showFilters);
                        // allow time for UI to update
                        setTimeout(() => {
                          adjustTableHeaderHeight();
                        }, 0.1);
                      }
                    }}
                    aria-label='search-button'
                    aria-valuetext='search-button'
                  >
                    {showInlineFilters ? (
                      <FilterSolid width='16px' height='15px' />
                    ) : (
                      <Filter width='16px' height='15px' />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {displayInModal && <ShareButton />}
              {columnSelectorConfig && (
                <ColumnSelector
                  columnVisibility={{ [selectedTab]: selectedColumnVisibility }}
                  columnSelector={columnSelectorConfig?.columnSelector}
                  type={selectedTab}
                  defaultDisabledKey={columnSelectorConfig?.defaultDisabledKey}
                  selectableColumns={selectableColumnsKeys}
                  updateLastColumnsChecked={() => {
                    // not sure why this is needed
                  }}
                  updateColumnVisibility={(k) => {
                    updateHiddenColumns(k, !hiddenColumns?.[k]);
                  }}
                  visibleColumnCount={selectableColumns.length} // TODO may want to limit in future with a prop
                />
              )}
            </>
          )}
        </Grid>
      </Grid>

      {!isLoading && displayCustomFilterBar && (
        <Grid size={{ xs: 12 }}>
          <CustomFilterToolbar
            filters={filterConfig?.filters}
            appliedFilters={appliedFilters}
            handleUpdateFilters={updateCustomFilters}
            adjustTableHeaderHeight={adjustTableHeaderHeight}
            tableFilterTypeMetaDataBuilder={tableFilterTypeMetaDataBuilder}
          />
        </Grid>
      )}

      {!isLoading && showInlineFilters && !isMobileScreen && (
        <Grid container size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column' }}>
          <TableFilters
            {...filterConfig}
            selectedValues={appliedFilters}
            sxProps={{ container: { mb: 1, mr: 1 } }}
            onFilterChange={handleFilterChange}
          />
        </Grid>
      )}

      {!isLoading && displayInModal && (
        <TableCreateFiltersModal<T>
          onClose={() => setShowModalFiltersAnchorEl(null)}
          isOpen={!!showModalFiltersAnchorEl}
          anchorEl={showModalFiltersAnchorEl}
          filterConfig={filterConfig}
          selectedValues={appliedFilters}
          handleUpdateFilters={updateCustomFilters}
        />
      )}
    </Grid>
  );
}

const gridToolbarContainer = {
  display: 'table',
  justifyContent: 'space-between',
  padding: '8px 24px',
  maxWidth: '100%',
  width: '100%',
  flexDirection: { xs: 'row-reverse', md: 'row' },
  overflow: 'show',
} as const;

const chipToolbarContainer = {
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  paddingTop: 1,
  marginBottom: 1,
} as const;

const filtersToolbarContainer = {
  marginTop: { xs: 1, sm: -1, md: -1 },
  paddingTop: '0px !important',
  textAlign: 'right',
  justifyContent: 'flex-end',
  maxWidth: { xs: '100%', md: 350 },
  display: 'flex',
};
